<template>
  <v-layout justify-end>
    <v-btn outline slot="activator" color="primary" class="mb-2" @click="open">
      <v-icon dark left>note_add</v-icon>New Item
    </v-btn>
    <v-dialog v-model="localDialog" persistent max-width="1000px" scrollable>
      <v-card>
        <v-card-title>
          <span class="headline">{{ formTitle }}</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-container fluid>
            <v-form ref="form">
              <v-layout row wrap>
                <v-flex>
                  <image-uploader
                    v-bind:constImage="local.image"
                    v-bind:dialog="dialog"
                    v-bind:errorMsg="errorMsg"
                    @image="assignImage">
                  </image-uploader>
                  <v-text-field
                    label="Name"
                    v-model="local.name"
                    :rules="generalRules"
                    required>
                  </v-text-field>
                  <v-text-field
                    label="Service (Optional)"
                    v-model="local.service">
                  </v-text-field>
                  <v-textarea
                    label="Description (Optional)"
                    v-model="local.description">
                  </v-textarea>
                </v-flex>
                <v-spacer></v-spacer>
                <v-divider vertical></v-divider>
                <v-spacer></v-spacer>
                <v-flex>
                  <h3>Type of Rigs</h3>
                  <v-radio-group v-model="local.rig">
                    <v-radio :label="rigTypes.humanoid" :value="rigTypes.humanoid"></v-radio>
                    <v-radio :label="rigTypes.custom" :value="rigTypes.custom"></v-radio>
                  </v-radio-group>
                  <br>
                  <h3>Polygon Limitation</h3>
                  <v-text-field
                    v-model="local.polygon_count"
                    label="Polygon Count"
                    type="number">
                  </v-text-field>
                  <br>
                  <h3>Dimension in meters</h3>
                  <v-text-field
                    v-model="local.dimension.X"
                    label="Dimension X"
                    type="number">
                  </v-text-field>
                  <v-text-field
                    v-model="local.dimension.Y"
                    label="Dimension Y"
                    type="number">
                  </v-text-field>
                  <v-text-field
                    v-model="local.dimension.Z"
                    label="Dimension Z"
                    type="number">
                  </v-text-field>
                  <h3>Scale</h3>
                  <v-text-field
                    v-model="local.scale"
                    label="Character scale"
                    type="number" min=0>
                  </v-text-field>
                </v-flex>
                <v-spacer />
                <v-flex >
                  <div >
                  <h3 >Attachments</h3>
                  <!-- :rules="generalRules" used here in all 4 inputs -->
                  <file-uploader 
                    @input="assignFBX"
                    v-model="local.fbx">
                    <div slot="activator">
                      <v-text-field
                        label="File box"
                        v-model="local.fbx.fileName"
                        readonly prepend-icon="attach_file"
                        >
                      </v-text-field>
                    </div>
                  </file-uploader>
                  <file-uploader 
                    @input="assignIOS"
                    v-model="local.asset_bundle.iOS">
                    <div slot="activator">
                      <v-text-field
                        label="iOS Asset Bundle"
                        v-model="local.asset_bundle.iOS.fileName"
                        readonly prepend-icon="attach_file"
                        >
                      </v-text-field>
                    </div>
                  </file-uploader>
                  <file-uploader 
                    @input="assignAndroid"
                    v-model="local.asset_bundle.Android">
                    <div slot="activator">
                      <v-text-field
                        label="Android Asset Bundle"
                        v-model="local.asset_bundle.Android.fileName"
                        readonly prepend-icon="attach_file"
                        >
                      </v-text-field>
                    </div>
                  </file-uploader>
                  <file-uploader 
                    @input="assignWebGL"
                    v-model="local.asset_bundle.WebGL">
                    <div slot="activator">
                      <v-text-field
                        label="WebGL Asset Bundle"
                        v-model="local.asset_bundle.WebGL.fileName"
                        readonly prepend-icon="attach_file"
                        >
                      </v-text-field>
                    </div>
                  </file-uploader>
                  </div>
                    <popover :always="true">
                    <h3>Select Materials</h3>
                  </popover>
                  <v-select
                    :items="materials"
                    v-model="local.material"
                    label="Select Materials"
                    chips
                    persistent-hint>
                  </v-select>
                  <popover :always="true">
                    <h3>Interactive Surfaces</h3>
                  </popover>
                  <v-select
                    :items="interactive_surfaces"
                    v-model="local.interactive_surface"
                    label="Interactive Surfaces"
                    chips
                    persistent-hint>
                  </v-select>
                </v-flex>
              </v-layout>
            </v-form>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" outline @click="close">Cancel</v-btn>
          <v-btn  v-if="showSaveButton" color="primary" @click="save">
            <v-flex v-if="this.saving">
              <v-progress-circular
                indeterminate
                color="primary">
              </v-progress-circular>
            </v-flex>
            <v-flex v-else>Save</v-flex>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import ImageUploader from '../uploader/imageUploader'
import FileUploader from '../uploader/fileUploader'

export default {
  name: 'addCharacterModel',
  components: { ImageUploader, FileUploader },
  data: () => ({
    valid: true,
    errorMsg: '',
    rigTypes: {
      humanoid: 'Humanoid',
      custom: 'Custom'
    },
    generalRules: [
      v => !!v || 'This field is required'
    ],
    saving: false,
    local: {
      id: null,
      name: '',
      service: '',
      image: null,
      description: '',
      rig: 'Humanoid',
      polygon_count: 1,
      dimension: {
        X: 0,
        Y: 0,
        Z: 0
      },
      fbx: {
        fileName: '',
        file: '',
        fileURL: ''
      },
      asset_bundle: {
        iOS: {
          fileName: '',
          file: '',
          fileURL: ''
        },
        Android: {
          fileName: '',
          file: '',
          fileURL: ''
        },
        WebGL: {
          fileName: '',
          file: '',
          fileURL: ''
        }
      },
      material: '',
      interactive_surface: '',
      scale: 0,
    },
    materials: ['test', 'testing'],
    interactive_surfaces: ['interactive', 'surfaces']
  }),
  props: {
    dialog: '',
    editedID: '',
    showSaveButton: true,
    info: {
      id: null,
      name: '',
      service: '',
      image: null,
      description: '',
      rig: 'Humanoid',
      scale: 0,
      polygon_count: 1,
      dimension: {
        X: 0,
        Y: 0,
        Z: 0
      },
      fbx: {
        fileName: '',
        file: '',
        fileURL: ''
      },
      asset_bundle: {
        iOS: {
          fileName: '',
          file: '',
          fileURL: ''
        },
        Android: {
          fileName: '',
          file: '',
          fileURL: ''
        },
        WebGL: {
          fileName: '',
          file: '',
          fileURL: ''
        }
      }
    }
  },
  computed: {
    formTitle () {
      return this.editedID === -1 ? 'New Character Model' : this.showSaveButton ? 'Edit Character Model' : 'Preview Character Model'
    },
    localDialog: {
      get: function () {
        return this.dialog
      }
    }
  },
  watch: {
    info (val) {
      this.local = Object.assign({}, val)
    },
    dialog (val) {
      if (!val) {
        this.saving = false
        this.resetForm()
      }
    }
  },
  methods: {
    resetForm () {
      this.$refs.form.resetValidation()
      this.errorMsg = ''
    },
    assignImage (payload) {
      this.local.image = payload
      this.errorMsg = ''
    },
    assignFBX (payload) {
      this.local.fbx = payload
    },
    assignIOS (payload) {
      this.local.asset_bundle.iOS = payload
    },
    assignAndroid (payload) {
      this.local.asset_bundle.Android = payload
    },
    assignWebGL (payload) {
      this.local.asset_bundle.WebGL = payload
    },
    open () {
      this.$emit('open')
    },
    close () {
      this.$emit('close')
    },
    validateForm () {
      let flag = this.$refs.form.validate()
      if (this.local.image === null) {
        flag = false
        this.errorMsg = 'Please add a picture'
      }
      return flag
    },
    save () {
      if (this.validateForm()) {
        this.saving = true
        this.$emit('save', this.local)
      }
    }
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/assets/add-new/addCharacterModel.vue